import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, TrendingDown, TrendingUp, Tag } from "lucide-react";
import { fmtINR, fmtDate } from "@/lib/format";
import { toast } from "sonner";

const TAG_OPTIONS = [
  { value: "wastage",     label: "Wastage" },
  { value: "rework",      label: "Rework" },
  { value: "breakage",    label: "Breakage" },
  { value: "spec_change", label: "Spec change" },
  { value: "yield_gain",  label: "Yield gain" },
  { value: "measurement", label: "Measurement" },
  { value: "other",       label: "Other" },
];

/**
 * Iter-97 · Assembly Variance Report.
 * BOM issues with FG yield drift beyond configurable tolerance %.
 */
export default function AssemblyVariance() {
  const [data, setData] = useState(null);
  const [tol, setTol] = useState(5);

  const load = () => api.get(`/reports/assembly-variance?tolerance_pct=${tol}`)
    .then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tol]);

  const tagRow = async (issue_id, tag) => {
    try {
      await api.post(`/reports/assembly-variance/${issue_id}/tag`, { tag });
      toast.success(`Tagged as ${tag.replaceAll("_", " ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Tag failed"); }
  };

  if (!data) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="assembly-variance">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">// Assembly variance</div>
          <div className="text-sm text-muted-foreground">Planned vs actual FG qty · flags drift beyond tolerance</div>
        </div>
        <div>
          <Label className="text-[10px]">Tolerance %</Label>
          <Input type="number" step="0.5" value={tol} onChange={(e) => setTol(Number(e.target.value))}
                   className="rounded-sm h-8 w-24 tabular-nums" data-testid="av-tolerance" />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <Card className="rounded-sm p-3 border-amber-500/30 bg-amber-500/10">
          <div className="text-[10px] font-mono uppercase text-amber-800 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Flagged</div>
          <div className="text-2xl font-bold tabular-nums text-amber-900 mt-1" data-testid="av-flagged-count">{data.flagged_count}</div>
        </Card>
        <Card className="rounded-sm p-3">
          <div className="text-[10px] font-mono uppercase text-muted-foreground">Issues scanned</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{(data.rows || []).length}</div>
        </Card>
        <Card className="rounded-sm p-3 border-red-500/30 bg-red-500/10">
          <div className="text-[10px] font-mono uppercase text-red-800">Value impact ₹</div>
          <div className="text-2xl font-bold tabular-nums text-red-900 mt-1">{fmtINR(data.total_value_impact || 0)}</div>
        </Card>
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr>
            <th>Date</th><th>Issue #</th><th>FG Item</th>
            <th className="text-right">Planned</th><th className="text-right">Actual</th>
            <th className="text-right">Variance</th><th className="text-right">%</th>
            <th className="text-right">Value ₹</th><th>Status</th>
            <th>Root cause</th>
          </tr></thead>
          <tbody>
            {(data.rows || []).length === 0 && <tr><td colSpan="10" className="text-center py-10 text-muted-foreground">No BOM issues yet.</td></tr>}
            {(data.rows || []).map((r) => (
              <tr key={r.issue_id} className={r.flagged ? "bg-amber-500/5" : ""} data-testid={`av-row-${r.issue_number}`}>
                <td className="text-xs">{fmtDate(r.date)}</td>
                <td className="font-mono text-xs">{r.issue_number}</td>
                <td>{r.fg_item_name}</td>
                <td className="text-right tabular-nums">{r.planned_qty}</td>
                <td className="text-right tabular-nums">{r.actual_qty}</td>
                <td className={`text-right tabular-nums ${r.direction === "shortage" ? "text-red-700" : r.direction === "surplus" ? "text-emerald-700" : "text-muted-foreground"}`}>
                  {r.direction === "shortage" ? <TrendingDown className="w-3 h-3 inline mr-1" /> : r.direction === "surplus" ? <TrendingUp className="w-3 h-3 inline mr-1" /> : null}
                  {r.variance > 0 ? "+" : ""}{r.variance}
                </td>
                <td className={`text-right tabular-nums font-medium ${r.flagged ? "text-red-700" : ""}`}>
                  {r.variance_pct > 0 ? "+" : ""}{r.variance_pct}%
                </td>
                <td className={`text-right tabular-nums ${r.value_impact < 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtINR(r.value_impact)}</td>
                <td className="text-[10px] font-mono uppercase">{r.status}{r.flagged && <span className="ml-1 text-amber-700">· flagged</span>}</td>
                <td>
                  {r.flagged ? (
                    <Select value={r.variance_tag || ""} onValueChange={(v) => tagRow(r.issue_id, v)}>
                      <SelectTrigger className="rounded-sm h-7 w-[130px] text-xs" data-testid={`av-tag-${r.issue_number}`}>
                        <SelectValue placeholder="+ tag" />
                      </SelectTrigger>
                      <SelectContent>
                        {TAG_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : <span className="text-[10px] text-muted-foreground">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
